/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20260611 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CellphonesService } from '../cellphones.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { Cellphone } from 'src/app/_model/cellphone';
import { CellphonesDaoService } from '../../dao/cellphones-dao.service';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

@Injectable()
export class CellphonesLogicService implements CellphonesService {
  public cellphones$ = new BehaviorSubject<Cellphone[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private cellphonesDaoService: CellphonesDaoService,
    private authenticationService: AuthenticationService,
  ) {
    this.initialize();
  }

  public getCellphoneById(id: IMSId): Cellphone {
    if (id === undefined) {
      return undefined;
    }
    return this.cellphones$.getValue().find(
      (c) => c.id.imsId === id.imsId && c.id.secondaryId === id.secondaryId,
    );
  }

  public async reset(): Promise<void> {
    this.cellphonesDaoService.reset();
  }

  public async clear(): Promise<void> {
    await this.cellphonesDaoService.clear();
    this.cellphones$.next([]);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    if (apiLevel < APILevel.SYNCCELLPHONES) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to sync cellphones',
      );
      return;
    }
    await this.clear();
    await this.loadRemoteCellphones(syncKey);
  }

  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalCellphones();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  async initializeCompleted(): Promise<void> {}

  private async loadLocalCellphones(): Promise<void> {
    try {
      this.logger.log(this.constructor.name, '- Load local Cellphones');
      const cellphones = await this.cellphonesDaoService.getCellphones();
      this.cellphones$.next(cellphones);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Cellphones: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteCellphones(syncKey: string): Promise<void> {
    if (!this.authenticationService.authenticationState.getValue()) {
      return;
    }

    this.logger.log(this.constructor.name, '- Load Cellphones from IMS');
    try {
      const cellphonesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'synccellphones',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            synckey: syncKey,
          },
        }),
      );

      const cellphones: Cellphone[] = cellphonesData.map((dto: any) =>
        Cellphone.fromDto(dto),
      );

      this.logger.log(
        this.constructor.name,
        'Finished loading cellphones (' + cellphones.length + ' Total)',
      );

      await this.cellphonesDaoService.saveCellphones(cellphones);
      this.cellphones$.next(cellphones);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error downloading Cellphones: ',
        error?.message,
      );
      throw error;
    }
  }
}
